import { CommonModule, DatePipe, NgForOf, NgIf } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { AuthService } from "../../core/services/auth.service";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { RouterLink } from "@angular/router";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiLink,
  TuiLoader,
  tuiLoaderOptionsProvider,
  TuiSizeL,
  TuiSizeS,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBreadcrumbs,
  TuiDataListDropdownManager,
  TuiDataListWrapper,
  TuiFade,
  TuiItemsWithMore,
  TuiPagination,
  TuiStatus,
  TuiStringifyContentPipe,
  TuiSwitch,
  TuiTabs,
} from "@taiga-ui/kit";
import { TuiCell, TuiNavigation, TuiSearch } from "@taiga-ui/layout";
import { TuiTable } from "@taiga-ui/addon-table";
import {
  TuiComboBoxModule,
  TuiInputSliderModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { ForgotResetPasswordService } from "../../core/services/forgot-reset-password.service.service";
import { INurse } from "../../model/interface/nurse";
import { ClientService } from "../../core/services/client.service";
import { DisableUnlessPermissionDirective } from "../../core/directives/disable-unless-permission.directive";
import { loadListState, saveListState } from "../../shared/utils/list-state";
import { forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { formatClientIdentifier } from "../../shared/utils/client-identifier";
import {
  canUnarchiveClient,
  isPermanentTypeChangeArchive,
  TYPE_CHANGE_UNARCHIVE_MESSAGE,
} from "../../shared/utils/client-archive";
import { ChangeClientTypePanelComponent } from "../../shared/components/change-client-type-panel/change-client-type-panel.component";
import { ClientTypeChangeFlowService } from "../../shared/services/client-type-change-flow.service";

@Component({
  selector: 'app-client',
  imports: [
    DisableUnlessPermissionDirective,
    FormsModule,
    NgForOf,
    NgIf,
    TuiAppearance,
    TuiDataListWrapper,
    TuiBreadcrumbs,
    TuiButton,
    TuiDataList,
    TuiPagination,
    TuiDropdown,
    TuiFade,
    TuiTable,
    TuiLink,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiCell,
    TuiStatus,
    TuiItemsWithMore,
    TuiSearch,
    ReactiveFormsModule,
    TuiSwitch,
    CommonModule,
    TuiInputSliderModule,
    TuiTextfieldControllerModule,
    RouterLink,
    TuiComboBoxModule,
    TuiStringifyContentPipe,
    TuiLoader,
    TuiDataListDropdownManager,
    ChangeClientTypePanelComponent,
  ],
  templateUrl: './client.component.html',
  styleUrl: './client.component.scss',
  providers: [DatePipe, tuiLoaderOptionsProvider({ size: "xl" })],
})
export class ClientComponent {
  protected readonly breadcrumbs = ["Royal Hospital", "Clients"];
  protected readonly sizes = ["m"] as const;
  protected open = false;
  protected openDropdownsize: TuiSizeL | TuiSizeS = "s";
  protected openDropdown = false;
  protected size = this.sizes[0];
  isLoading = false;
  clientData: any;
  paginatedData: INurse[] = [];
  clientService = inject(ClientService);
  showResults: string = "";
  filters = {
    searchText: "",
    status: null as { status: string; value: boolean } | null,
    archived: false,
    clientType: null as { label: string; value: string } | null,
  };
  pageIndex = 1;
  pageSize = 10;
  total: number = 6;
  sortField = "createdAt";
  sortOrder: number = -1;
  openMap: { [nurseId: string]: boolean } = {};
  loggedInUserCanEdit = false;
  /** Multi-select bulk-action state. Keyed by client _id. */
  selectedIds = new Set<string>();
  bulkBusy = false;
  changeTypeDialogOpen = false;
  changeTypeClient: any | null = null;
  changeTypeBusy = false;
  private readonly typeChangeFlow = inject(ClientTypeChangeFlowService);

  // Patient Type filter options
  protected readonly clientTypeItems = [
    { label: "Registered", value: "Registered" },
    { label: "Non Registered", value: "Unregistered" },
    { label: "Inward", value: "Inward" },
  ];

  protected stringifyClientType(item: { label: string; value: string } | null): string {
    return item ? item.label : "";
  }
  private authService = inject(AuthService);

  constructor(
    private datePipe: DatePipe,
    private resetPasswordService: ForgotResetPasswordService,
    private alerts: TuiAlertService
  ) {}

  // Form setup
  protected readonly form = new FormGroup({
    search: new FormControl(""),
    select: new FormControl(""),
  });

  protected value = null;

  protected readonly items = [
    { status: "Active", value: true },
    { status: "Inactive", value: false },
  ];

  protected stringify(item: { status: string; value: boolean } | null): string {
    if (!item) return "";
    return item.status;
  }

  private readonly listStateKey = "client-list";

  ngOnInit(): void {
    this.restoreListState();
    this.loadClients();
    this.checkUserPermission();
  }

  private restoreListState(): void {
    const restored = loadListState(this.listStateKey, {
      filters: this.filters,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
    });
    this.filters = { ...this.filters, ...restored.filters };
    this.filters.searchText = ""
    this.pageIndex = restored.pageIndex;
    this.pageSize = restored.pageSize;
    this.sortField = restored.sortField;
    this.sortOrder = restored.sortOrder;
  }

  private rememberListState(): void {
    saveListState(this.listStateKey, {
      filters: this.filters,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
    });
  }

  loadClients(): void {
    // this.rememberListState();
    this.selectedIds = new Set<string>();
    const payload = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      filters: {
        searchText: this.filters.searchText,
        archived: this.filters.archived,
        status: this.filters.status ? this.filters.status.value : null,
        clientType: this.filters.clientType ? this.filters.clientType.value : null,
      },
      sortField: this.sortField,
      sortOrder: this.sortOrder,
    };

    this.isLoading = true;

    this.clientService.getPagedClients(payload).subscribe({
      next: (res: any) => {
        if (res && res.length > 0 && res[0].data) {
          this.clientData = res[0].data;
          this.total = res[0].metadata?.[0]?.total ?? 0;
        } else {
          this.clientData = [];
          this.total = 0;
        }
      },
      error: (error) => {
        console.error("Error fetching client data:", error);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  getRegistrationTypeBgColor(registrationType: string): string {
    switch (registrationType) {
      case 'Life Time':
        return 'purple';
      case 'One Year':
        return 'blue';
      default:
        return 'gray';
    }
  }

  /**
   * Color the badge by client type so all 3 types are visually distinguishable
   * and the colours are preserved when filters are applied.
   */
  getClientTypeBgColor(clientType: string): string {
    switch (clientType) {
      case 'Registered':
        return '#526ED3'; // blue
      case 'Unregistered':
        return '#F2994A'; // orange
      case 'Inward':
        return '#27AE60'; // green
      default:
        return '#a6afce';
    }
  }

  /** Canonical display: REG-0000, NREG-0000, BHT-00000 (via formatClientIdentifier). */
  getClientIdentifier(client: any): string {
    return formatClientIdentifier(client);
  }

  /**
   * Display label for the Patient Type cell. The backend stores the value as
   * "Unregistered" but every customer-facing surface in the app refers to it
   * as "Non Registered" (filter dropdown, reports, etc.), so we map it here
   * to keep the table consistent without changing the underlying data.
   */
  getClientTypeLabel(clientType: string | undefined | null): string {
    const value = clientType || 'Registered';
    return value === 'Unregistered' ? 'Non Registered' : value;
  }

  /**
   * Renders the "Registration Type" cell:
   *   Inward -> "Inward"
   *   Unregistered -> the nursingType ("With Home Nursing" / "Without Home Nursing")
   *   Registered -> the registrationType (Life Time / One Year)
   */
  getRegistrationTypeLabel(client: any): string {
    if (!client) return '';
    if (client.clientType === 'Inward') return 'Inward';
    if (client.clientType === 'Unregistered') return client.nursingType || 'Non Registered';
    return client.registrationType || '';
  }

  toggleDropdown(clientId: string): void {
    this.openMap[clientId] = !this.openMap[clientId];
  }

  sort(): void {
    this.sortOrder = this.sortOrder === 1 ? -1 : 1;
    this.loadClients();
  }

  applyFilters(newFilters: any): void {
    this.filters = { ...this.filters, ...newFilters };
    // Filtering is done server-side; always reset paging to first page
    // so users don't get stuck on an empty page 2/3.
    this.pageIndex = 1;
    this.loadClients();
  }
 

  applySort(): void {
    this.loadClients();
  }

  onEnter(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      this.applyFilters({ searchText: this.filters.searchText });
    }
  }

  onPageChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.loadClients();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = Number(pageSize);
    this.pageIndex = 1;
    this.loadClients();
  }


  clearSearch(): void {
    this.filters.searchText = "";
    this.filters.status = null;
    this.filters.clientType = null;
    this.applyFilters({
      searchText: this.filters.searchText,
      status: null,
      clientType: null,
    });
  }

  getPageCount(): number {
    return Math.ceil(this.total / this.pageSize);
  }

  //Dropdown functinalities
  resetUserPassword(email: any): void {
    const payload = {
      email: email,
    };
    this.resetPasswordService.requestResetPasswordClient(payload).subscribe({
      next: (response) => {
        this.alerts
          .open("Password reset information sent to client's email", {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
      },
      error: (error) => {
        console.error("Error resetting password:", error);
      },
    });
  }

  toggleActive(clientId: string): void {
    this.isLoading = true;
    this.clientService.getOneClientById(clientId).subscribe((client: any) => {
      if (!client) return;
      
      if (!client.active) {
        if (client.expiryDate) {
          const currentDate = new Date();
          const expiryDate = new Date(client.expiryDate);
          
          if (expiryDate < currentDate) {
            this.alerts
              .open("Cannot reactivate client. The expiry date has passed. Use Renew to extend membership and reactivate.", {
                label: "Warning",
                appearance: "warning",
              })
              .subscribe();
            return;
          }
        }
      }
      
      const updatedClient = { ...client, active: !client.active };

      this.clientService.updateClient(updatedClient).subscribe({
        next: (response) => {
          this.clientData = response;
          const status = updatedClient.active ? "activated" : "deactivated";
          this.alerts
            .open(`Client has been ${status} successfully.`, {
              label: "Success",
              appearance: "success",
            })
            .subscribe();
          this.loadClients();
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error("Error updating client data:", error);
          this.alerts
            .open("Failed to update client data.", {
              label: "Error",
              appearance: "error",
            })
            .subscribe();
        },
      });
    });
  }

  openChangeClientType(user: any): void {
    if (!user?._id || user?.archived) return;
    this.openMap[user._id] = false;
    this.changeTypeClient = user;
    this.changeTypeDialogOpen = true;
  }

  onChangeTypeConfirm(targetType: string): void {
    const client = this.changeTypeClient;
    if (!client?._id || this.changeTypeBusy) return;
    this.changeTypeBusy = true;
    this.typeChangeFlow.execute(String(client._id), targetType).subscribe({
      next: (res) => {
        this.changeTypeBusy = false;
        this.changeTypeDialogOpen = false;
        this.changeTypeClient = null;
        if (res?.success && res.data) {
          this.typeChangeFlow.showSuccess(
            res.data.message ||
              "Previous client archived. Complete the new client record.",
          );
          this.typeChangeFlow.navigateToAddForm(res);
        } else {
          this.typeChangeFlow.showError(
            res?.message || "Failed to prepare client type change.",
          );
        }
      },
      error: (err) => {
        this.changeTypeBusy = false;
        this.typeChangeFlow.showError(
          err?.error?.message ||
            err?.message ||
            "Failed to prepare client type change.",
        );
      },
    });
  }

  onChangeTypeCancel(): void {
    this.changeTypeClient = null;
    this.changeTypeDialogOpen = false;
  }

  readonly isPermanentTypeChangeArchive = isPermanentTypeChangeArchive;
  readonly canUnarchiveClient = canUnarchiveClient;

  toggleArchive(clientId: string): void {
    this.clientService.getOneClientById(clientId).subscribe((client: any) => {
      if (!client) return;
      if (client.archived && !canUnarchiveClient(client)) {
        this.alerts
          .open(TYPE_CHANGE_UNARCHIVE_MESSAGE, {
            label: "Cannot unarchive",
            appearance: "warning",
          })
          .subscribe();
        return;
      }
      const updatedClient = { ...client, archived: !client.archived };

      this.clientService.updateClient(updatedClient).subscribe({
        next: (response) => {
          // this.clientData = response;
          const status = updatedClient.archived ? "archived" : "unarchived";
          this.alerts
            .open(`Client has been ${status} successfully.`, {
              label: "Success",
              appearance: "success",
            })
            .subscribe();
          this.loadClients();
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error("Error updating client data:", error);
          this.alerts
            .open(
              error?.error?.message ||
                error?.message ||
                "Failed to update client data.",
              {
                label: "Error",
                appearance: "error",
              },
            )
            .subscribe();
        },
      });
    });
  }

  /* ---------- Multi-select bulk actions ---------- */

  isSelected(clientId: string): boolean {
    return this.selectedIds.has(clientId);
  }

  toggleSelected(clientId: string, checked: boolean): void {
    if (checked) this.selectedIds.add(clientId);
    else this.selectedIds.delete(clientId);
  }

  isAllSelected(): boolean {
    if (!this.clientData || this.clientData.length === 0) return false;
    return this.clientData.every((c: any) => this.selectedIds.has(c._id));
  }

  toggleSelectAll(checked: boolean): void {
    if (!this.clientData) return;
    if (checked) {
      this.clientData.forEach((c: any) => this.selectedIds.add(c._id));
    } else {
      this.clientData.forEach((c: any) => this.selectedIds.delete(c._id));
    }
  }

  selectionCount(): number {
    return this.selectedIds.size;
  }

  /**
   * Bulk archive/unarchive: fetches each selected client to preserve existing
   * fields and toggles `archived`. Uses the existing single-row endpoint in a
   * forkJoin loop - no backend changes.
   */
  bulkArchiveSelected(archive: boolean): void {
    if (this.selectedIds.size === 0 || this.bulkBusy) return;
    const verb = archive ? "archive" : "unarchive";
    if (!confirm(`Are you sure you want to ${verb} ${this.selectedIds.size} client(s)?`)) return;
    this.bulkBusy = true;
    const ids = Array.from(this.selectedIds);
    const ops = ids.map((id) =>
      this.clientService.getOneClientById(id).pipe(
        catchError(() => of(null)),
      ),
    );
    forkJoin(ops).subscribe({
      next: (clients: any[]) => {
        const rows = clients.filter((c: any) => !!c);
        const eligible = archive
          ? rows
          : rows.filter((c: any) => canUnarchiveClient(c));
        const skipped = rows.length - eligible.length;
        if (!archive && skipped > 0) {
          this.alerts
            .open(
              `${skipped} client(s) were skipped (archived due to type change and cannot be restored).`,
              { label: "Notice", appearance: "warning" },
            )
            .subscribe();
        }
        const updates = eligible.map((c: any) =>
          this.clientService
            .updateClient({ ...c, archived: archive })
            .pipe(catchError(() => of(null))),
        );
        if (updates.length === 0) {
          this.bulkBusy = false;
          return;
        }
        forkJoin(updates).subscribe({
          next: () => {
            this.alerts
              .open(`${updates.length} client(s) ${archive ? "archived" : "unarchived"}.`, {
                label: "Success",
                appearance: "success",
              })
              .subscribe();
            this.bulkBusy = false;
            this.loadClients();
          },
          error: () => {
            this.bulkBusy = false;
            this.alerts
              .open(`Bulk ${verb} failed for some rows.`, {
                label: "Error",
                appearance: "error",
              })
              .subscribe();
          },
        });
      },
      error: () => {
        this.bulkBusy = false;
      },
    });
  }

  /**
   * Export currently visible (paged) selection - or all visible rows if no
   * row is checked - to a CSV file. Frontend-only: no backend changes.
   */
  exportSelectedToCsv(): void {
    if (!this.clientData || this.clientData.length === 0) return;
    const rows: any[] =
      this.selectedIds.size > 0
        ? this.clientData.filter((c: any) => this.selectedIds.has(c._id))
        : this.clientData;
    if (rows.length === 0) return;

    const headers = [
      "Identifier",
      "Client Type",
      "Client Name",
      "Patient Name",
      "Client Contact No",
      "Registration Type",
      "Status",
      "Created Date",
    ];
    const escape = (val: any): string => {
      const s = (val ?? "").toString().replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [headers.join(",")];
    for (const r of rows) {
      lines.push(
        [
          this.getClientIdentifier(r),
          r.clientType || "Registered",
          r.guardianName || "",
          r.name || "",
          r.guardianContactNo || "",
          this.getRegistrationTypeLabel(r),
          r.active ? "Active" : "Inactive",
          this.datePipe.transform(r.createdAt, "yyyy-MM-dd") || "",
        ]
          .map(escape)
          .join(","),
      );
    }
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `clients-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
  checkUserPermission(): void {
    this.loggedInUserCanEdit = this.authService.hasPermission("CLIENT_UPDATE");
  }

}
